import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Building2, ChevronDown, ChevronRight, Copy, Check, Plus, Save } from "lucide-react";

interface TabConfig {
  enabled: boolean;
  apr_rate?: number;
  early_payment_days?: number;
  budget?: number;
}

interface TabSettings {
  insights?: TabConfig;
  strategize?: TabConfig;
  optimize?: TabConfig;
  integrations?: TabConfig;
}

interface Company {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  tab_settings: TabSettings;
  user_count: number;
}

const DASHBOARD_TABS: { key: keyof TabSettings; label: string; description: string }[] = [
  { key: "insights", label: "Insights", description: "CFO metrics, missed savings analysis" },
  { key: "strategize", label: "Strategize", description: "Outstanding invoices and PAR pipeline" },
  {
    key: "optimize",
    label: "Optimize",
    description: "Operator invoice management and discounting",
  },
  { key: "integrations", label: "Integrations", description: "ERP and system integrations" },
];

function shortId(id: string) {
  return id.slice(0, 8) + "…";
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getDefaultTabSettings(settings: TabSettings, key: keyof TabSettings): TabConfig {
  return settings[key] ?? { enabled: true };
}

interface CompanyRowProps {
  company: Company;
  onUpdated: () => void;
}

function CompanyRow({ company, onUpdated }: CompanyRowProps) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [tabSettings, setTabSettings] = useState<TabSettings>(company.tab_settings);
  const [isSaving, setIsSaving] = useState(false);

  const copyId = () => {
    navigator.clipboard.writeText(company.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  };

  const setTabEnabled = (key: keyof TabSettings, enabled: boolean) => {
    setTabSettings((prev) => ({
      ...prev,
      [key]: { ...getDefaultTabSettings(prev, key), enabled },
    }));
  };

  const setTabField = (key: keyof TabSettings, field: string, value: number) => {
    setTabSettings((prev) => ({
      ...prev,
      [key]: { ...getDefaultTabSettings(prev, key), [field]: value },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { error } = await supabase
      .from("backtest_sessions" as never)
      .update({ tab_settings: tabSettings as never } as never)
      .eq("id", company.id);
    setIsSaving(false);
    if (error) {
      toast({ title: "Error saving settings", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Company settings saved" });
      onUpdated();
    }
  };

  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full flex items-center gap-4 px-5 py-3 hover:bg-muted/30 text-left transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{company.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <code className="text-[11px] text-muted-foreground font-mono">
              {shortId(company.id)}
            </code>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyId();
              }}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Copy full ID"
            >
              {copiedId ? (
                <Check className="w-3 h-3 text-emerald-500" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>
        <Badge variant="secondary" className="text-[10px] shrink-0">
          {company.user_count} {company.user_count === 1 ? "user" : "users"}
        </Badge>
        <span className="text-xs text-muted-foreground shrink-0">
          {formatDate(company.created_at)}
        </span>
      </button>

      {expanded && (
        <div className="px-5 pb-5 pt-1 bg-muted/20 space-y-5">
          {/* Description */}
          {company.description && (
            <p className="text-xs text-muted-foreground">{company.description}</p>
          )}

          {/* Tab Visibility & Settings */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Dashboard Tabs
            </h4>
            <div className="space-y-4">
              {DASHBOARD_TABS.map(({ key, label, description }) => {
                const cfg = getDefaultTabSettings(tabSettings, key);
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{label}</p>
                        <p className="text-xs text-muted-foreground">{description}</p>
                      </div>
                      <Switch
                        checked={cfg.enabled}
                        onCheckedChange={(checked) => setTabEnabled(key, checked)}
                      />
                    </div>

                    {/* Per-tab settings (only shown when enabled) */}
                    {cfg.enabled && key === "optimize" && (
                      <div className="ml-1 pl-3 border-l border-border grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground">
                            Default APR (%)
                          </label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={cfg.apr_rate ?? 12}
                            min={0}
                            max={100}
                            onChange={(e) => setTabField(key, "apr_rate", Number(e.target.value))}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground">
                            Early Payment Days
                          </label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={cfg.early_payment_days ?? 25}
                            min={1}
                            max={365}
                            onChange={(e) =>
                              setTabField(key, "early_payment_days", Number(e.target.value))
                            }
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground">Budget ($)</label>
                          <Input
                            type="number"
                            className="h-7 text-xs"
                            value={cfg.budget ?? 0}
                            min={0}
                            onChange={(e) => setTabField(key, "budget", Number(e.target.value))}
                          />
                        </div>
                      </div>
                    )}

                    {cfg.enabled && key === "insights" && (
                      <div className="ml-1 pl-3 border-l border-border">
                        <p className="text-[11px] text-muted-foreground">
                          No additional settings for this tab.
                        </p>
                      </div>
                    )}

                    {cfg.enabled && key === "strategize" && (
                      <div className="ml-1 pl-3 border-l border-border">
                        <p className="text-[11px] text-muted-foreground">
                          No additional settings for this tab.
                        </p>
                      </div>
                    )}

                    {cfg.enabled && key === "integrations" && (
                      <div className="ml-1 pl-3 border-l border-border">
                        <p className="text-[11px] text-muted-foreground">
                          No additional settings for this tab.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            <Save className="w-3.5 h-3.5 mr-1" />
            {isSaving ? "Saving…" : "Save Company Settings"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function CompanyManagementCard() {
  const { toast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    const [{ data: sessions }, { data: profiles }] = await Promise.all([
      supabase
        .from("backtest_sessions" as never)
        .select("id, name, description, created_at, tab_settings")
        .order("name"),
      supabase.from("profiles").select("company_id"),
    ]);

    const countMap: Record<string, number> = {};
    for (const p of (profiles ?? []) as { company_id: string | null }[]) {
      if (p.company_id) countMap[p.company_id] = (countMap[p.company_id] ?? 0) + 1;
    }

    const rows = (
      (sessions ?? []) as {
        id: string;
        name: string;
        description: string | null;
        created_at: string;
        tab_settings: TabSettings | null;
      }[]
    ).map((s) => ({
      ...s,
      tab_settings: s.tab_settings ?? {},
      user_count: countMap[s.id] ?? 0,
    }));

    setCompanies(rows);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setIsSaving(true);
    const { error } = await supabase
      .from("backtest_sessions" as never)
      .insert({ name, tab_settings: {} } as never);
    setIsSaving(false);
    if (error) {
      toast({
        title: "Error creating company",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Company created", description: name });
    setNewName("");
    setShowForm(false);
    fetchCompanies();
  };

  return (
    <div className="space-y-0">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="text-xs text-muted-foreground">
              Manage tenant companies and their dashboard configuration
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowForm((v) => !v)}>
          <Plus className="w-3.5 h-3.5 mr-1" /> New Company
        </Button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 rounded-lg border border-border bg-muted/30 flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-muted-foreground">Company name</label>
            <Input
              placeholder="Acme Corp"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              autoFocus
            />
          </div>
          <Button size="sm" onClick={handleCreate} disabled={isSaving || !newName.trim()}>
            {isSaving ? "Creating…" : "Create"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowForm(false);
              setNewName("");
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : companies.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No companies yet. Create one to start onboarding clients.
          </div>
        ) : (
          <div>
            {companies.map((c) => (
              <CompanyRow key={c.id} company={c} onUpdated={fetchCompanies} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
